/**
 * The platform's system instructions in the Behavior tab (Robert, 16.10.2026): five short default behaviours every
 * agent gets (the classic default system prompt, folded) plus one protection switch. The owner switches each one on
 * or off and can open its exact wording; none can be edited or deleted. The owner's own rules above take precedence.
 * Folded by default: the header with "x of y on" and a chevron opens the hint and the list, gliding open and shut
 * (the body stays mounted; folded it is inert, so neither keyboard nor screen reader reaches it).
 * Backend: GET /system-instructions?lang=…; switching goes through POST /toggle {kind: 'system_instruction'}; which
 * ones are off is in the spec (system_instructions_off).
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { studioApi } from './studioApi';
import { load, peek } from './studioCache';
import { Collapsible, Toggle } from './ui';

const cacheKey = (lang) => `sysinstr:${lang}`;

export default function SystemInstructions({ botId, spec, onToggle }) {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || 'en').slice(0, 2);
  const [data, setData] = useState(() => peek(cacheKey(lang)) ?? null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    load(cacheKey(lang), () => studioApi.get(botId, `/system-instructions?lang=${lang}`))
      .then((d) => alive && setData(d)).catch(() => {});
    return () => { alive = false; };
  }, [botId, lang]);

  const items = data?.instructions || [];
  if (!items.length) return null;
  const off = new Set(spec.system_instructions_off || []);
  const on = items.filter((i) => !off.has(i.id)).length;
  const bodyId = `sysinstr-${botId}`;

  return (
    <section className="as-rule-section" aria-label={t('agent_studio_sysinstr_title')}>
      <button type="button" className="as-rule-section-head is-toggle" aria-expanded={open} aria-controls={bodyId}
        onClick={() => setOpen((o) => !o)}>
        <span className="as-rule-section-title">{t('agent_studio_sysinstr_title')}</span>
        <span className="as-rule-section-count">
          {t('agent_studio_sysinstr_count', { on, total: items.length })}
          <FontAwesomeIcon icon={faChevronDown} className={`as-rule-section-caret ${open ? 'is-open' : ''}`} />
        </span>
      </button>
      <div id={bodyId} className={`as-fold ${open ? 'is-open' : ''}`} aria-hidden={!open} {...(open ? {} : { inert: '' })}>
        <div className="as-fold-inner as-rule-section-body">
          <p className="as-panel-hint">{t('agent_studio_sysinstr_hint')}</p>
          <div className="as-rules-card">
            {items.map((i) => {
              const enabled = !off.has(i.id);
              return (
                <div key={i.id} className={`as-rule ${enabled ? '' : 'is-off'}`}>
                  <Collapsible
                    title={<span className="as-rule-title">{i.title}</span>}
                    right={<span className="as-row-actions"><Toggle checked={enabled} title={i.title} onChange={(v) => onToggle(i.id, v)} /></span>}
                  >
                    <p className="as-rule-text">{i.summary}</p>
                    <p className="as-sysinstr-prompt"><span>{t('agent_studio_sysinstr_wording')}</span> {i.prompt}</p>
                  </Collapsible>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
